import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Mask } from "@/utils/mask";
import { updatePaymentAdvance, showPaymentAdvance } from "@/Api/paymentAdvances";
import { getClientsDataWithFilters } from "@/Api/clients";
import { getSellersDataWithFilters } from "@/Api/sellers";
import { PaymentAdvance, PaymentAdvanceResponse } from "@/types/paymentAdvance";
import { AsyncSearchableSelect } from "@/components/ui/async-searchable-select";
import { useAuth } from "@/hooks/auth";
import { TypeUser } from "@/types/userType";

interface Client {
  id: number;
  uuid: string;
  name: string;
}

interface Seller {
  id: number;
  uuid: string;
  user?: { name: string };
}

interface UpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: PaymentAdvance | null;
  updateFront: React.Dispatch<React.SetStateAction<PaymentAdvanceResponse>>;
}

export const UpdateModal = ({
  isOpen,
  onClose,
  item,
  updateFront,
}: UpdateModalProps) => {
  const user = useAuth();
  const isSellerUser = user?.user?.user_type_id === TypeUser.seller;

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedSeller, setSelectedSeller] = useState<Seller | null>(null);
  const [observation, setObservation] = useState("");
  const [amount, setAmount] = useState("");

  useEffect(() => {
    if (isOpen && item) {
      setLoadingData(true);
      fetchItemData(item.uuid).finally(() => setLoadingData(false));
    }
  }, [isOpen, item]);

  const fetchItemData = async (uuid: string) => {
    try {
      const { data, ok } = await showPaymentAdvance(uuid);
      if (ok && data?.response) {
        const fetchedItem = data.response;
        setObservation(fetchedItem.observation || "");
        setAmount(Mask.formatCurrency(fetchedItem.amount));

        if (fetchedItem.client) {
          setSelectedClient({
            id: fetchedItem.client_id,
            uuid: fetchedItem.client.uuid || "",
            name: fetchedItem.client.name,
          });
        }

        if (fetchedItem.seller) {
          setSelectedSeller({
            id: fetchedItem.seller_id,
            uuid: fetchedItem.seller.uuid || "",
            user: { name: fetchedItem.seller.user?.name || "" },
          });
        }
      }
    } catch (error) {
      console.error("Error fetching item data:", error);
      toast.error("Erro ao carregar dados do Pix Gerado");
    }
  };

  const searchClients = useCallback(async (searchTerm: string): Promise<Client[]> => {
    try {
      const { data, ok } = await getClientsDataWithFilters({
        name: searchTerm,
        per_page: 20,
      });
      if (ok && data?.response?.data) {
        return data.response.data;
      }
      return [];
    } catch (error) {
      console.error("Error searching clients:", error);
      return [];
    }
  }, []);

  const searchSellers = useCallback(async (searchTerm: string): Promise<Seller[]> => {
    try {
      const { data, ok } = await getSellersDataWithFilters({
        name: searchTerm,
        per_page: 20,
      });
      if (ok && data?.response?.data) {
        return data.response.data;
      }
      return [];
    } catch (error) {
      console.error("Error searching sellers:", error);
      return [];
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!item) return;

    if (!selectedClient || !selectedSeller) {
      toast.error("Selecione o cliente e o vendedor");
      return;
    }

    setLoading(true);
    try {
      const { data, ok } = await updatePaymentAdvance(item.uuid, {
        client_id: selectedClient.id,
        seller_id: selectedSeller.id,
        observation: observation || undefined,
      });

      if (ok && data?.response) {
        toast.success("Pix Gerado atualizado com sucesso!");

        updateFront((prev) => ({
          ...prev,
          data: prev.data.map((p) =>
            p.uuid === item.uuid ? { ...p, ...data.response } : p
          ),
        }));

        onClose();
      }
    } catch (error) {
      toast.error("Erro ao atualizar Pix Gerado");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedClient(null);
    setSelectedSeller(null);
    setObservation("");
    setAmount("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Editar Pix Gerado #{item?.id}</DialogTitle>
        </DialogHeader>

        {loadingData ? (
          <div className="flex justify-center items-center py-8">
            <Loader className="h-8 w-8 animate-spin" />
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Valor</Label>
            <div className="p-3 bg-muted rounded-md text-sm font-medium">
              {amount || "R$ 0,00"}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Cliente *</Label>
            <AsyncSearchableSelect<Client>
              fetchItems={searchClients}
              displayField="name"
              placeholder="Buscar cliente..."
              searchPlaceholder="Digite o nome do cliente..."
              emptyMessage="Nenhum cliente encontrado."
              value={selectedClient}
              onSelect={setSelectedClient}
              getItemId={(item) => item.id}
            />
          </div>

          <div className="space-y-2">
            <Label>Vendedor *</Label>
            {isSellerUser ? (
              <Input
                value={selectedSeller?.user?.name || user?.user?.name || ""}
                disabled
                className="bg-gray-100"
              />
            ) : (
              <AsyncSearchableSelect<Seller>
                fetchItems={searchSellers}
                displayField={(seller) => seller.user?.name || `Vendedor #${seller.id}`}
                placeholder="Buscar vendedor..."
                searchPlaceholder="Digite o nome do vendedor..."
                emptyMessage="Nenhum vendedor encontrado."
                value={selectedSeller}
                onSelect={setSelectedSeller}
                getItemId={(item) => item.id}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit_observation">Observação</Label>
            <Textarea
              id="edit_observation"
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              placeholder="Observação opcional..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className={cn(colors.bg_primary)}
            >
              {loading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin mr-2" />
                  Salvando...
                </>
              ) : (
                "Salvar"
              )}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
